import type { Directive, DirectiveBinding } from 'vue';

type BreakpointKey = 'Small' | 'Medium' | 'Large' | 'ExtraLarge';

type SkeletonSize = {
	width: string;
	height: string;
};

type SkeletonSizes = Partial<Record<BreakpointKey, SkeletonSize>>;

type SkeletonMargins = Partial<Record<BreakpointKey, string>>;

type SkeletonStyles = Partial<Record<BreakpointKey, string>>;

export type SkeletonBinding = {
	loading: boolean;
	sizes?: SkeletonSizes;
	margins?: SkeletonMargins;
	styles?: SkeletonStyles;
};

const defaultSize: SkeletonSize = { width: '2.5rem', height: '2.5rem' };
const defaultMargin = '0';

type ResolvedSkeletonConfig = {
	widths: Record<'sm' | 'md' | 'lg' | 'xl', string>;
	heights: Record<'sm' | 'md' | 'lg' | 'xl', string>;
	margins: Record<'sm' | 'md' | 'lg' | 'xl', string>;
	styles: Record<'sm' | 'md' | 'lg' | 'xl', string>;
};

// Media queries mirror the @include Breakpoint() ranges used in _skeleton.scss.
const breakpointMediaQueries: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
	sm: 'all and (max-width: 480px)',
	md: 'all and (min-width: 481px) and (max-width: 768px)',
	lg: 'all and (min-width: 769px) and (max-width: 1024px)',
	xl: 'all and (min-width: 1025px)',
};

const configClassMap = new Map<string, string>();
const elementConfigClass = new WeakMap<HTMLElement, string>();
let styleTag: HTMLStyleElement | null = null;
let classCounter = 0;

const sanitizeCssValue = (value: string): string => {
	// Prevent malformed CSS declarations while preserving normal CSS values.
	return value.replace(/[;{}]/g, '').trim();
};

const sanitizeCssBlock = (value: string): string => {
	// Allow full declaration lists (semicolons) but block rule injection via braces.
	return value
		.replace(/[{}]/g, '')
		.split(';')
		.map((declaration) => declaration.trim())
		.filter(Boolean)
		// Force !important so custom styles always win over the base Skeleton mixin.
		.map((declaration) => (/!important$/.test(declaration) ? declaration : `${declaration} !important`))
		.join(';');
};

const ensureStyleSheet = (): CSSStyleSheet | null => {
	if (typeof document === 'undefined') return null;

	if (!styleTag) {
		styleTag = document.createElement('style');
		styleTag.setAttribute('data-skeleton-configs', 'true');

		document.head.appendChild(styleTag);
	}

	return styleTag.sheet;
};

const resolveConfig = (value?: SkeletonBinding): ResolvedSkeletonConfig => {
	const sizes = value?.sizes ?? {};
	const margins = value?.margins ?? {};
	const styles = value?.styles ?? {};

	const sm = sizes.Small ?? defaultSize;
	const md = sizes.Medium ?? sm;
	const lg = sizes.Large ?? md;
	const xl = sizes.ExtraLarge ?? lg;

	const mSm = margins.Small ?? defaultMargin;
	const mMd = margins.Medium ?? mSm;
	const mLg = margins.Large ?? mMd;
	const mXl = margins.ExtraLarge ?? mLg;

	const sSm = styles.Small ?? '';
	const sMd = styles.Medium ?? sSm;
	const sLg = styles.Large ?? sMd;
	const sXl = styles.ExtraLarge ?? sLg;

	return {
		widths: {
			sm: sanitizeCssValue(sm.width),
			md: sanitizeCssValue(md.width),
			lg: sanitizeCssValue(lg.width),
			xl: sanitizeCssValue(xl.width),
		},
		heights: {
			sm: sanitizeCssValue(sm.height),
			md: sanitizeCssValue(md.height),
			lg: sanitizeCssValue(lg.height),
			xl: sanitizeCssValue(xl.height),
		},
		margins: {
			sm: sanitizeCssValue(mSm),
			md: sanitizeCssValue(mMd),
			lg: sanitizeCssValue(mLg),
			xl: sanitizeCssValue(mXl),
		},
		styles: {
			sm: sanitizeCssBlock(sSm),
			md: sanitizeCssBlock(sMd),
			lg: sanitizeCssBlock(sLg),
			xl: sanitizeCssBlock(sXl),
		},
	};
};

const serializeConfig = (config: ResolvedSkeletonConfig): string => {
	return [
		config.widths.sm,
		config.heights.sm,
		config.widths.md,
		config.heights.md,
		config.widths.lg,
		config.heights.lg,
		config.widths.xl,
		config.heights.xl,
		config.margins.sm,
		config.margins.md,
		config.margins.lg,
		config.margins.xl,
		config.styles.sm,
		config.styles.md,
		config.styles.lg,
		config.styles.xl,
	].join('|');
};

const ensureConfigClass = (config: ResolvedSkeletonConfig): string => {
	const key = serializeConfig(config);
	const existingClass = configClassMap.get(key);

	if (existingClass) return existingClass;

	const className = `block${++classCounter}`;
	configClassMap.set(key, className);

	const stylesheet = ensureStyleSheet();
	if (!stylesheet) return className;

	const rule = [
		`.SkeletonBlock.${className}{`,
		`--skel-w-sm:${config.widths.sm};`,
		`--skel-h-sm:${config.heights.sm};`,
		`--skel-w-md:${config.widths.md};`,
		`--skel-h-md:${config.heights.md};`,
		`--skel-w-lg:${config.widths.lg};`,
		`--skel-h-lg:${config.heights.lg};`,
		`--skel-w-xl:${config.widths.xl};`,
		`--skel-h-xl:${config.heights.xl};`,
		`--skel-m-sm:${config.margins.sm};`,
		`--skel-m-md:${config.margins.md};`,
		`--skel-m-lg:${config.margins.lg};`,
		`--skel-m-xl:${config.margins.xl};`,
		`}`
	].join('');
	stylesheet.insertRule(rule, stylesheet.cssRules.length);

	(['sm', 'md', 'lg', 'xl'] as const).forEach((breakpoint) => {
		const customStyle = config.styles[breakpoint];
		if (!customStyle) return;

		const mediaRule = `@media ${breakpointMediaQueries[breakpoint]}{.SkeletonBlock.${className}.loading{${customStyle}}}`;
		stylesheet.insertRule(mediaRule, stylesheet.cssRules.length);
	});

	return className;
};

const applySkeleton = (el: HTMLElement, value?: SkeletonBinding): void => {
	const loading = !!value?.loading;
	const configClass = ensureConfigClass(resolveConfig(value));
	const previousConfigClass = elementConfigClass.get(el);

	el.classList.add('SkeletonBlock');

	if (previousConfigClass && previousConfigClass !== configClass) {
		el.classList.remove(previousConfigClass);
	}

	el.classList.add(configClass);
	elementConfigClass.set(el, configClass);

	el.classList.toggle('loading', loading);
};

export const skeletonDirective: Directive<HTMLElement, SkeletonBinding> = {
	mounted: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
	updated: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
	unmounted: (el) => {
		const configClass = elementConfigClass.get(el);

		if (configClass) {
			el.classList.remove(configClass);
			elementConfigClass.delete(el);
		}

		el.classList.remove('loading');
	},
};
